import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';

import {
  ChangesDirection,
  ColumnTypes,
  Data,
  HighlightChangesDirection,
  TariffDetails,
  VolumeDifference,
} from './column-content.container';
import { MainTableColumnId } from '@prlw/core/monitor/types/main-table';
import { BasisDetailsInterface } from '../../../basis-details.interface';
import { Destination } from '@prlw/core/destinations/destination.entity';
import { Modifier, ModifierType } from '@prlw/core/monitor/types/common';
import { Observable, skip } from 'rxjs';
import { MainTableModifiersService } from '@prlw/core/monitor/main-table-modifiers.service';

const CHANGE_PRICE_LOAD_TIME = 1000;
@Component({
  selector: 'prlw-table-column-content-ui',
  templateUrl: './column-content.component.html',
  styleUrls: ['./column-content.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ColumnContentComponent {
  @Input() public modifierType!: ModifierType | null;
  @Input() public data!: Data[];
  @Input() public columnId!: MainTableColumnId;
  @Input() public columnTypes!: ColumnTypes;
  @Input() public hasData: boolean = true;
  @Input() public hasSecondData: boolean = false;
  @Input() public hasHighlight: boolean = false;
  @Input() public basisTrend: string | null = null;
  @Input() public isOfferActive: boolean = false;
  @Input() public isLastDeal: boolean = false;
  @Input() public isOldDeal: boolean = false;
  @Input() public isLastQuote: boolean = false;
  @Input() public isOldQuote: boolean = false;
  @Input() public changesDirection!: ChangesDirection;
  @Input() public highlightChangesDirection!: HighlightChangesDirection;
  @Input() public isDemandActive: boolean = false;
  @Input() public volumeDifference!: VolumeDifference;
  @Input() public isTariffLoading: boolean = true;
  @Input() public isTariffError: boolean = false;
  @Input() public destination: Destination | null = null;
  @Input() public isDealTotal: boolean = false;
  @Input() public isDealTotalPrice: boolean = false;
  @Input() public dealTime: string | null = null;
  @Input() public prevDealDate: string | null = null;
  @Input() public prevDealsAmountDiffDirection!: ChangesDirection;
  @Input() public lastQuoteTime: string | null = null;
  @Input() public basisDetails: BasisDetailsInterface | null = null;
  @Input() public tariffDetails: TariffDetails | null = null;
  @Input() public isHovered!: boolean;
  @Input() public isActive!: boolean;
  @Input() public mode!: string;
  @Input() public isEditor: boolean | null = null;
  @Input() public instrumentCode: string | null = null;

  @Output() public eventOpenEditTraderTariffOverlay =
    new EventEmitter<string>();
  handleOpenEditTraderTariffOverlay(e: Event, instrumentCode: string | null) {
    e.stopPropagation();
    if (instrumentCode) {
      this.eventOpenEditTraderTariffOverlay.emit(instrumentCode);
    }
  }

  public currentModifier = false;
  public readonly modifier$: Observable<Modifier>;

  constructor(mainTableModifiersService: MainTableModifiersService) {
    this.modifier$ = mainTableModifiersService.modifier$;
    this.modifier$.pipe(skip(1)).subscribe(() => {
      this.currentModifier = true;
      setTimeout(() => {
        this.currentModifier = false;
      }, CHANGE_PRICE_LOAD_TIME);
    });
  }

  public get localPriceChanging(): boolean {
    return this.currentModifier;
  }

  public get mainData(): Data {
    return this.data[0];
  }

  public get secondData(): Data {
    return this.data[1];
  }

  public get isCommonCell(): boolean {
    return (
      !this.columnTypes.isChanges &&
      !this.columnTypes.isTradeStart &&
      !this.columnTypes.isPrevDealsAmountDiff
    );
  }

  public get isCommonPlug(): boolean {
    return !this.isTariffLoading && !this.isTariffError;
  }

  public get hasProgressBar(): boolean {
    return this.hasData && this.columnTypes.isVolume;
  }

  public get hasChangesHighlight(): boolean {
    return (
      this.highlightChangesDirection.isHigh ||
      this.highlightChangesDirection.isLow
    );
  }

  public get hasTrendHighlight(): boolean {
    return this.hasHighlight && !this.hasChangesHighlight;
  }

  public get hasSecondDataHighlight(): boolean {
    return this.hasTrendHighlight && this.hasSecondData;
  }

  public wrapNumber(data: Data): number {
    return Number(data);
  }

  get asterisks() {
    if (this.isOldDeal || this.isOldQuote) return '**';
    if (this.isLastDeal || this.isLastQuote) return '*';
    return '';
  }

  isPipe() {
    return this.tariffDetails?.deliveryMethod === 'U';
  }

  isEditable() {
    return this.tariffDetails?.deliveryMethod !== 'U';
  }
}
